import Enumerable from "linq";
import store from "../../../../store";
import { IInternalScriptProcessible } from "..";
import { ExecuteRequest, ExternalScriptExecuteResult, ScriptLinkItem } from "../../../../domain/localhost/items";
import { IConfiguration } from "../../../../domain/configuration";
import { IExternalSystemService } from "../../../../domain/externalSystem";
import { Arguments } from ".";
import { LoggerFactory } from "../../../../utilities/logger";
import { IExternalSystemGetResultBean } from "../../../externalSystem/ver2";

export class ExternalSystemValueGetter implements IInternalScriptProcessible {
  protected readonly _logger = LoggerFactory.getLogger();
  
  constructor(private _externalSystemService: IExternalSystemService) {
  }

  isProcessible(request: ExecuteRequest): boolean {
    const configuration = (store.state.configuration as IConfiguration).behavior.localhost.internalScript.scripts.externalSystemValueGetter;
    return configuration.isEnable
        && (new RegExp(configuration.pathExpression, "i")).test(this.toModuleName(request.exePath));
  }

  private toArguments(request: ExecuteRequest): Arguments {
    return new Arguments(request.arguments);
  }

  private toModuleName(path: string): string {
    if (!path) { return path; }
    const splitted = path.split("\\");
    return Enumerable.from(splitted).lastOrDefault();
  }

  async process(request: ExecuteRequest): Promise<ExternalScriptExecuteResult> {
    try {
      const apiUrl = this.getApiUrl(request);
      this._logger.debug("[EXTERNAL API] " + apiUrl);
      const rawResult = await this.getValueFromExternalSystem(apiUrl);
      return this.toExternalScriptExecuteResult(request, rawResult);
    } catch (e) {
      this._logger.error(e);
      const errorResult = new ExternalScriptExecuteResult();
      errorResult.isComplete = false;
      return errorResult;
    }
  }

  private getApiUrl(request: ExecuteRequest): string {
    const apiFormat = this.getApiFormat(request);
    if (!apiFormat) { return apiFormat; }

    const parameterValues = this.getParameterValues(request);
    let convertingText = apiFormat;
    for (let i = 0; i < parameterValues.length; ++i) {
      const replaceTargetRegex = new RegExp("\\{" + i + "\\}", "g");
      const replaceText = parameterValues[i] ? parameterValues[i] : "";
      convertingText = convertingText.replace(replaceTargetRegex, replaceText);
    }
    return convertingText;
  }

  private getApiFormat(request: ExecuteRequest): string {
    return this.getArgumentSpecifiedObjectValue(request, "-apiFormatObjectName");
  }

  private getParameterValues(request: ExecuteRequest): string[] {
    return this.getArgumentSpecifiedObjectValues(request, "-parameterValueObjectNames");
  }

  private getArgumentSpecifiedObjectValue(request: ExecuteRequest, argumentKey: string): string {
    const args = this.toArguments(request);
    const objectName = args.getValue(argumentKey);
    return this.getScriptLinkItemValueByObjectName(request.scriptLinkItems, objectName);
  }

  private getScriptLinkItemValueByObjectName(items: ScriptLinkItem[], objectName: string): string {
    const found = Enumerable.from(items)
      .where(i => i.name === objectName)
      .firstOrDefault();
    if (!found) { return null; }
    return (found.value != null)
      ? found.value + ""   // 文字列に変換している
      : "";
  }

  private getArgumentSpecifiedObjectValues(request: ExecuteRequest, argumentKey: string): string[] {
    const args = this.toArguments(request);
    const objectNames = args.getValue(argumentKey);
    if (!objectNames) { return []; }

    const splitted = objectNames.split(",");
    return splitted.map(s => this.getScriptLinkItemValueByObjectName(request.scriptLinkItems, s));
  }

  private async getValueFromExternalSystem(apiUrl: string): Promise<any> {
    if (!apiUrl) { throw new Error("apiUrl is empty!!!"); }
    const result = await this._externalSystemService.getData<IExternalSystemGetResultBean>(apiUrl);
    if (!result.isOk || !result.result) { throw new Error("値を取得できませんでした。message=" + result.message); }
    return result.result;
  }

  private toExternalScriptExecuteResult(request: ExecuteRequest, rawResult: any): ExternalScriptExecuteResult {
    if (!request) { throw new Error("request is empty!!!"); }
    if (!rawResult) { throw new Error("rawResult is empty!!!"); }
    // Jsonのオブジェクトで結果が返ってくるのかと思いきや、Jsonのテキストで返却されてくるので対策している
    const resultAsObject = (typeof rawResult === "string")
      ? JSON.parse(rawResult)
      : rawResult;
    const result = new ExternalScriptExecuteResult();
    result.isComplete = true;
    result.values = [];
    result.scriptLinkResults = Enumerable.from(request.scriptLinkItems)
      .select(i => this.createUpdatedClone(i, resultAsObject))
      .where(i => !!i)
      .toArray();
    return result;
  }

  createUpdatedClone(source: ScriptLinkItem, resultAsObject: any): ScriptLinkItem {
    if (!source.name) { return null; }
    const value = resultAsObject[source.name];
    if (value === undefined) { return null; }
    const cloned = source.clone();
    // TODO: 円選択・チェックの対応はこれでよい？
    cloned.value = !!cloned.itemName
      ? ((cloned.itemName === value) ? "1" : "0")
      : value + ""; // 文字列に変換している
    return cloned;
  }
}
